import React, { useEffect } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MUIDataTableColumn } from "mui-datatables";
import { ThemedTable } from "../../../../../common/tables/ThemedTable";
import { LoadingSpinner } from "../../../../../common";
import { Link as RouterLink } from "react-router-dom";
import { Routes } from "../../../../../../routes";
import { companyDialogTableOptions, headerCellProps } from "../../utils/companyDialogTableOptions";
import { useSelector } from "react-redux";
import { organisationsSelector } from "../../../../../../redux/organisations/selectors/organisation.selectors";
import { IPersonBadge } from "common/types";
import CloseIcon from "@mui/icons-material/Close";
import { useExhibitorDetailsData } from "../../hooks/useExhibitorDetailsData";
import { Nullable } from "@reedexpo/mercury-badge";
import { sharersDialogStyles } from "./styles/CompanySharers.styles";

type CompanySharersDialogProps = {
  open: boolean;
  onClose: () => void;
  participatingOrganisationId: Nullable<string>;
  companyName?: string;
  mshOrganisation?: IPersonBadge["organisation"];
};

export const CompanySharersDialog: React.FC<CompanySharersDialogProps> = ({
  open,
  onClose,
  participatingOrganisationId,
  companyName,
  mshOrganisation
}) => {
  const { t } = useTranslation("common");
  const { sharers, isSharersLoading, fetchSharers } = useExhibitorDetailsData(participatingOrganisationId);

  const organisations = useSelector(organisationsSelector);

  const columns: MUIDataTableColumn[] = [
    {
      name: "name",
      label: t("badgeForm.organisation.displayName"),
      options: {
        sort: false,
        filter: false,
        setCellHeaderProps: headerCellProps,
        customBodyRender: (value: string, tableMeta) => {
          const row = sharers[tableMeta.rowIndex];
          const selectedCompany = {
            organisation: {
              displayName: row!.name,
              participatingOrganisationId: row!.id
            }
          };

          return (
            <Link
              component={RouterLink}
              to={Routes.company}
              state={{ company: selectedCompany, mshOrganisation }}
              onClick={onClose}
              sx={sharersDialogStyles.link}
              data-testid="sharer-company-link"
            >
              {value}
            </Link>
          );
        }
      }
    },
    {
      name: "activeBadges",
      label: t("search.badges"),
      options: {
        sort: false,
        filter: false,
        setCellHeaderProps: headerCellProps
      }
    }
  ];

  useEffect(() => {
    if (!open || !participatingOrganisationId) return;
    fetchSharers();
  }, [open, participatingOrganisationId, organisations]);

  const hasSharers = sharers.length > 0;
  const showNoSharersState = !isSharersLoading && !hasSharers;
  const dialogTitle = showNoSharersState ? t("companyPage.sharersDialog.noSharersTitle") : t("companyPage.sharersDialog.title");

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onClose();
      }}
      maxWidth={showNoSharersState ? "xs" : "sm"}
      fullWidth
    >
      <DialogTitle sx={sharersDialogStyles.dialogTitleContainer}>
        {dialogTitle}
        <CloseIcon color="primary" onClick={onClose} sx={{ cursor: "pointer" }} />
      </DialogTitle>

      <DialogContent sx={{ pt: 0.5 }}>
        {isSharersLoading && (
          <Box sx={sharersDialogStyles.loading}>
            <LoadingSpinner />
          </Box>
        )}

        {showNoSharersState && (
          <Typography variant="body1" sx={sharersDialogStyles.noSharersMessage}>
            {t("companyPage.sharersDialog.noSharersMessage")}
          </Typography>
        )}

        {!isSharersLoading && hasSharers && (
          <>
            <Typography variant="body2" sx={sharersDialogStyles.subtitle}>
              {t("companyPage.sharersDialog.subtitle", { companyName: companyName || "" })}
            </Typography>

            <ThemedTable cols={columns} tableData={sharers} options={companyDialogTableOptions} />
          </>
        )}
      </DialogContent>

      <DialogActions sx={sharersDialogStyles.dialogActions}>
        <Button variant="contained" onClick={onClose} sx={sharersDialogStyles.closeButton}>
          {t("common.buttonLabels.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
